import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  ArrowLeft, 
  Search, 
  AlertTriangle,
  FileX,
  RefreshCw,
  Clock,
  Shield
} from 'lucide-react';
import Layout from '../components/Layout';

const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/30">
        <div className="container mx-auto px-4 py-8">
          
          {/* Header moderne */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
            <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                      <AlertTriangle className="h-8 w-8 text-white" />
                    </div>
                  
                    <div>
                    <h1 className="text-2xl font-bold">Erreur 404</h1>
                    <p className="text-red-100">Page introuvable</p>
                  </div>
                </div>

                <div className="hidden md:flex items-center space-x-3">
                  <div className="bg-white/10 rounded-lg px-3 py-2">
                    <span className="text-white text-sm font-medium">Code: 404</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contenu principal restructuré */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Section principale - Message d'erreur */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              <div className="text-center">
                {/* Illustration moderne */}
                <div className="mb-8">
                  <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-red-100 to-red-200 rounded-full mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center">
                      <FileX className="h-10 w-10 text-white" />
              </div>
            </div>

                  <div className="text-8xl font-black text-gray-200 mb-4 select-none">
                    404
                  </div>
                </div>

                {/* Message principal */}
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    Page introuvable
                </h2>
                  <p className="text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                  Désolé, la page que vous recherchez n'existe pas ou a été déplacée. 
                    La ressource demandée est introuvable sur ce serveur.
                </p>
              </div>

              {/* Actions principales */}
                <div className="space-y-6">
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate(-1)}
                      className="inline-flex items-center justify-center px-8 py-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  <ArrowLeft className="h-5 w-5 mr-2" />
                      Page précédente
                </button>
                    
                <button
                  onClick={() => navigate('/dashboard')}
                      className="inline-flex items-center justify-center px-8 py-4 bg-[#202654] text-white rounded-lg hover:bg-[#1a1f4a] transition-colors font-medium shadow-lg"
                >
                  <Home className="h-5 w-5 mr-2" />
                  Tableau de bord
                </button>
              </div>

                  <button
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Recharger la page
                  </button>
                </div>
              </div>

              {/* Suggestions intégrées */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 text-lg">💡</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h4 className="text-lg font-semibold text-blue-800 mb-3">Que faire maintenant ?</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-blue-700">
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                          Vérifiez l'orthographe de l'URL
                        </div>
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                          Utilisez le menu de navigation
                        </div>
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                          Revenez à la page précédente
                        </div>
                        <div className="flex items-center">
                          <span className="w-2 h-2 bg-blue-400 rounded-full mr-2"></span>
                          Contactez le support si nécessaire
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section latérale - Informations */}
            <div className="space-y-6">
              
              {/* Informations système */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-6 py-4">
                  <div className="flex items-center text-white">
                    <div className="bg-white/10 p-2 rounded-lg mr-3">
                      <Search className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Détails de l'erreur</h3>
                      <p className="text-gray-200 text-sm">Informations techniques</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Code d'erreur:</span>
                        <span className="font-semibold text-gray-900">404</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type:</span>
                        <span className="font-semibold text-gray-900">Not Found</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Statut:</span>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Erreur
                        </span>
                      </div>
                      <div className="pt-3 border-t border-gray-200">
                        <span className="text-gray-500 text-xs">URL demandée:</span>
                        <p className="font-mono text-xs text-gray-900 mt-1 p-2 bg-gray-100 rounded border break-all">
                          {window.location.pathname}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Horodatage et version */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
                  <div className="flex items-center text-white">
                    <div className="bg-white/10 p-2 rounded-lg mr-3">
                      <Clock className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">Informations</h3>
                      <p className="text-indigo-100 text-sm">Système et horodatage</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Version:</span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        B-School v1.0
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Timestamp:</span>
                      <span className="font-mono text-xs text-gray-900">
                        {new Date().toLocaleString('fr-FR')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Protocole:</span>
                      <span className="font-semibold text-gray-900">HTTPS</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-gray-200 text-center">
                    <div className="flex items-center justify-center text-gray-400 mb-2">
                      <Shield className="h-4 w-4 mr-1" />
                      <span className="text-xs">Connexion sécurisée</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Si le problème persiste, contactez l'administrateur système.
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NotFoundPage; 